// library functions
import { makeSprite, sendToFront } from "html-living-framework/lib/Utils/PixiUtils";

// pixi stuff
import { OutlineFilter } from "html-living-framework/lib/Filters/OutlineFilter";

// gameobjects
import { Draggable } from "html-living-framework/lib/Input/Draggable";

// pubsub topics
import * as Topics from "Data/Topics";
import * as HLF from "html-living-framework";

// types and interfaces
import { Application } from "Application";
import { GameConfig, ItemID, LocationID } from "Data/GameConfig";
import type { IItemConfig } from "Interfaces/IItemConfig";
import type { IItemState } from "Interfaces/IItemState";

export class InteriorItemIconReturnItems extends Draggable {
    public readonly id: ItemID;
    public readonly sprite: PIXI.Sprite;

    public get returned() { return this._returned; }

    protected _subscriptions: any[] = [];
    protected _returned: boolean;
    protected _returnAnchorY?: number;

    constructor(data: IItemConfig & IItemState, location: LocationID, options?: {
        highlightFloatingItems?: boolean,
        ICON_SIZE: number,
        returnItemAnchorY?: number,
    } & any) {
        super();
        this.id = data.id;
        const spritefilename = location + "/" + data.icon_int_return;
        const spritecontainer = this.visuals.addChild(new PIXI.Container());
        this.sprite = spritecontainer.addChild(makeSprite(spritefilename, GameConfig.spritesheets[location]));

        const xScale = options.ICON_SIZE / this.sprite.width;
        const yScale = options.ICON_SIZE / this.sprite.height;
        spritecontainer.scale.set(Math.min(xScale, yScale));

        this._returnAnchorY = options.returnItemAnchorY;

        this.hitArea = new PIXI.Rectangle(
            -spritecontainer.width / 2,
            -spritecontainer.height / 2,
            spritecontainer.width,
            spritecontainer.height,
        );

        this._returned = data.returned;

        if (data.found && !data.returned) {
            this.interactive = true;
            this._outlineFilter.padding = 16;
        }
        else {
            this.interactive = false;
        }

        this._hoverVo = data.vo?.returned;

        if (options?.highlightFloatingItems) {
            const outline = new OutlineFilter();
            outline.uniforms.outlineColor = [1, 1, 1]; // white
            outline.uniforms.thickness = [0.005, 0.005];
            outline.uniforms.filterClamp = [0, 0, 1, 1];
            outline.padding = 128;
            spritecontainer.filters = [outline];
        }

        this._subscriptions.push(
            PubSub.subscribe(Topics.ITEM_RETURNED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.DRAGGABLE_SELECTED, this.onMessageReceived.bind(this)),
            PubSub.subscribe(HLF.DRAG_BEGIN, this.onMessageReceived.bind(this)),
        );

        this.on("pointercancel", this.onPointerUpOutside);
    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean) {
        this._subscriptions.forEach(PubSub.unsubscribe);
        super.destroy(pOptions);
    }

    public select() {
        super.select();
        PubSub.publishSync(Topics.ITEM_SELECTED, this);
        sendToFront(this);
    }

    protected dragEnd(): void {
        super.dragEnd();
        this.deselect();
        this._visuals.position.set(0);
    }

    protected dragBegin() {
        super.dragBegin();
        sendToFront(this);
    }

    protected onMessageReceived(pTopic: string, pData: any) {
        switch (pTopic) {
            case Topics.ITEM_RETURNED:
                this.OnItemReturned(pData as IItemState);
                break;
            case HLF.DRAGGABLE_SELECTED:
            case HLF.DRAG_BEGIN:
                if (pData !== this) {
                    this.dragEnd();
                }
                break;
        }
    }

    protected OnItemReturned(state: IItemState) {
        if (state.id === this.id) {
            this.interactive = false;
            this._returned = true;
            this.deselect();
            if (this._returnAnchorY !== undefined) {
                this.sprite.anchor.y = this._returnAnchorY;
            }
            PubSub.publishSync(HLF.UNREGISTER_FOCUSABLE, this);
        }
    }

    protected playHoverVo(): void {
        if (this._hoverVo !== undefined) {
            Application.instance.voiceover.playVO(this._hoverVo, {
                priority: -1,
                mode: HLF.PlayMode.Override,
            });
            PubSub.publishSync(Topics.RESTART_IDLE_TIMER, undefined);
        }
    }
}
